import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";
import { formatDate, formatFixed } from "@/lib/i18n";
import { useApiMutation } from "@/lib/use-api-mutation";
import { useApiQuery } from "@/lib/use-api-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { File, Folder } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * `/projects/:slug/files`: the project's Seafile folder. Only what the
 * viewer may see is listed: everything for a manager, a member's picked
 * folders and the way down to them.
 */

export const Route = createFileRoute("/_app/projects/$slug/files")({
  validateSearch: (s: Record<string, unknown>) => ({
    path: typeof s.path === "string" ? s.path : "",
  }),
  component: FilesPage,
});

type Entry = {
  name: string;
  path: string;
  type: "file" | "dir";
  size: number;
  mtime: number;
  access: "full" | "partial";
  sharedWithAll: boolean;
};
type Listing = {
  path: string;
  access: "full" | "partial";
  canManage: boolean;
  entries: Entry[];
};

function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${formatFixed(bytes / 1024, 0)} kB`;
  if (bytes < 1024 ** 3) return `${formatFixed(bytes / 1024 ** 2, 1)} MB`;
  return `${formatFixed(bytes / 1024 ** 3, 1)} GB`;
}

function FilesPage() {
  const { t } = useTranslation();
  const { slug } = Route.useParams();
  const { path } = Route.useSearch();
  const base = `/api/projects/${encodeURIComponent(slug)}/files`;
  const { data, error, loading, reload } = useApiQuery(
    () => api.get<Listing>(`${base}?path=${encodeURIComponent(path)}`),
    [slug, path],
  );
  const crumbs = path ? path.split("/") : [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <nav className="flex flex-wrap items-center gap-1 text-sm" data-testid="files-breadcrumbs">
          <Link
            to="/projects/$slug/files"
            params={{ slug }}
            search={{ path: "" }}
            className="hover:underline"
          >
            {t("projFiles.title")}
          </Link>
          {crumbs.map((part, i) => (
            <span key={crumbs.slice(0, i + 1).join("/")} className="flex items-center gap-1">
              <span className="text-muted-foreground">/</span>
              <Link
                to="/projects/$slug/files"
                params={{ slug }}
                search={{ path: crumbs.slice(0, i + 1).join("/") }}
                className="hover:underline"
              >
                {part}
              </Link>
            </span>
          ))}
        </nav>
        {data?.access === "full" && (
          <a
            href={`${base}/open?path=${encodeURIComponent(path)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline"
          >
            {t("projFiles.openInSeafile")}
          </a>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading && !data && (
        <p className="text-sm text-muted-foreground">{t("projFiles.loading")}</p>
      )}

      {data && (
        <Card>
          <CardContent className="divide-y p-0">
            {data.entries.length === 0 && (
              <p className="p-4 text-sm text-muted-foreground" data-testid="files-empty">
                {t("projFiles.empty")}
              </p>
            )}
            {data.entries.map((e) => (
              <div
                key={e.path}
                className="flex items-center justify-between gap-3 px-4 py-2 text-sm"
                data-testid="file-entry"
              >
                {e.type === "dir" ? (
                  <Link
                    to="/projects/$slug/files"
                    params={{ slug }}
                    search={{ path: e.path }}
                    className="flex items-center gap-2 font-medium hover:underline"
                  >
                    <Folder className="h-4 w-4 text-muted-foreground" />
                    {e.name}
                  </Link>
                ) : (
                  <a
                    href={`${base}/download?path=${encodeURIComponent(e.path)}`}
                    className="flex items-center gap-2 hover:underline"
                  >
                    <File className="h-4 w-4 text-muted-foreground" />
                    {e.name}
                  </a>
                )}
                <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                  {e.type === "dir" && (e.sharedWithAll || data.canManage) && (
                    <ShareToggle
                      base={base}
                      entry={e}
                      canManage={data.canManage}
                      onChanged={reload}
                    />
                  )}
                  {e.type === "file"
                    ? size(e.size)
                    : e.access === "partial"
                      ? t("projFiles.someFolders")
                      : ""}
                  {e.mtime ? ` · ${formatDate(e.mtime * 1000)}` : ""}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ShareToggle({
  base,
  entry,
  canManage,
  onChanged,
}: { base: string; entry: Entry; canManage: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const toggle = useApiMutation(
    () => api.put(`${base}/shared`, { path: entry.path, shared: !entry.sharedWithAll }),
    {
      onSuccess: onChanged,
      onError: (msg) =>
        toast({ title: t("projFiles.shareFailed"), description: msg, variant: "destructive" }),
    },
  );
  if (!canManage) return <Badge variant="secondary">{t("projFiles.allMembers")}</Badge>;
  return (
    <Button
      type="button"
      variant={entry.sharedWithAll ? "secondary" : "ghost"}
      size="sm"
      disabled={toggle.pending}
      onClick={() => void toggle.mutate()}
      title={entry.sharedWithAll ? t("projFiles.sharedHint") : t("projFiles.shareHint")}
    >
      {entry.sharedWithAll ? t("projFiles.sharedWithAll") : t("projFiles.shareWithAll")}
    </Button>
  );
}
